import * as React from 'react';
import { Input as AntInput, Progress, Spin, message } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, FilterOutlined, SendOutlined, TeamOutlined } from '@ant-design/icons';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';

import { Input } from 'components/input/input';
import { Select } from 'components/select/select';
import { Modal } from 'components/modal/modal';
import { Button, buttonType } from 'components/button/button';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { Enum, EnumAsString, User } from 'core/domain';
import { getAllLevels, getAllUserCategories } from 'core/repositories/user';
import { getUsersPage, sendBulkSmsPart } from 'core/repositories/bulk-sms';
import { faNumToEnNum } from 'core/utils/decimalExtentions';
import { jFormat, jFormatEndOfDay } from 'core/utils/jFormat';

const { useEffect, useMemo, useRef, useState } = React;

const MAX_RECEIVERS_PER_REQUEST = 190;
const REQUEST_DELAY_MS = 2000;
const SENDER_LINES = ['1000777070707'] as const;
const DEFAULT_SENDER_LINE = SENDER_LINES[0];
/** Page size for the user search dropdown (infinite scroll). */
const SELECT_PAGE_SIZE = 20;
/** Page size when loading phone numbers from the user filters. */
const BULK_PHONES_PAGE_SIZE = 10000;
const MAX_BULK_PHONE_FETCH_PAGES = 500;

type Option = { label: string; value: string; disabled?: boolean };

interface Chunk {
    partNumber: number;
    receivers: string[];
}

interface Batch {
    batchId: string;
    totalParts: number;
    message: string;
    senderLine: string;
}

interface SendProgress {
    total: number;
    processed: number;
    accepted: number;
    failed: number;
    unresolved: string[];
}

/** Split lines, trim, skip empties, dedupe (first wins). Persian digits are normalized. */
const parseDistinctLines = (raw: string): string[] => {
    const seen = new Set<string>();
    for (const line of raw.split(/\r\n|\r|\n/)) {
        const trimmed = faNumToEnNum(line).trim();
        if (trimmed) seen.add(trimmed);
    }
    return Array.from(seen);
};

// پیامک فارسی (یونیکد) ۷۰ کاراکتری است و در حالت چندبخشی هر بخش ۶۷ کاراکتر؛ لاتین ۱۶۰ و ۱۵۳.
const getSmsLength = (text: string) => {
    const isUnicode = /[\u0080-\uFFFF]/.test(text);
    const single = isUnicode ? 70 : 160;
    const multi = isUnicode ? 67 : 153;
    const parts = text.length === 0 ? 0 : text.length <= single ? 1 : Math.ceil(text.length / multi);
    return { length: text.length, parts };
};

const newBatchId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID().replace(/-/g, '')
        : `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;

const fa = (value: number) => value.toLocaleString('fa-IR');

interface Props {
    /** بعد از ارسال، سوابق همان ارسال را در تب سوابق نشان می‌دهد. */
    onShowHistory: (batchId: string) => void;
}

export const SendForm: React.FC<Props> = ({ onShowHistory }) => {
    // Receivers
    const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>([]);
    const [manualInput, setManualInput] = useState('');

    // User search (infinite scroll)
    const [userOptions, setUserOptions] = useState<Option[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const searchTimer = useRef<NodeJS.Timeout | null>(null);
    // فقط پاسخ آخرین جستجو اعمال می‌شود.
    const searchToken = useRef(0);

    // User filters → bulk-fill the manual list
    const [levels, setLevels] = useState<EnumAsString[]>([]);
    const [categories, setCategories] = useState<Enum[]>([]);
    const [metaLoading, setMetaLoading] = useState(false);
    const [levelId, setLevelId] = useState<string>();
    const [categoryId, setCategoryId] = useState<number>();
    const [registerFrom, setRegisterFrom] = useState('');
    const [registerTo, setRegisterTo] = useState('');
    const [fetchPhonesLoading, setFetchPhonesLoading] = useState(false);

    // Message
    const [senderLine, setSenderLine] = useState<string>(DEFAULT_SENDER_LINE);
    const [smsMessage, setSmsMessage] = useState('');

    // Sending
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState<SendProgress>();
    const [failedChunks, setFailedChunks] = useState<Chunk[]>([]);
    const [batch, setBatch] = useState<Batch>();

    const manualStats = useMemo(() => {
        if (!manualInput) return { total: 0, empty: 0, duplicates: 0, sendable: 0 };
        const lines = manualInput.split(/\r\n|\r|\n/);
        const empty = lines.filter((l) => l.trim().length === 0).length;
        const sendable = parseDistinctLines(manualInput).length;
        return { total: lines.length, empty, duplicates: Math.max(lines.length - empty - sendable, 0), sendable };
    }, [manualInput]);

    const receivers = useMemo(
        () => Array.from(new Set([...selectedPhoneNumbers, ...parseDistinctLines(manualInput)])),
        [selectedPhoneNumbers, manualInput],
    );
    const partsCount = Math.ceil(receivers.length / MAX_RECEIVERS_PER_REQUEST);
    const smsLength = useMemo(() => getSmsLength(smsMessage.trim()), [smsMessage]);

    const loadUsers = async (nextPage: number, keyword: string) => {
        const token = ++searchToken.current;
        setSearchLoading(true);
        const data = await getUsersPage(
            nextPage,
            SELECT_PAGE_SIZE,
            keyword ? `?keywords=${encodeURIComponent(keyword)}` : '',
        );
        if (token !== searchToken.current) return;
        setSearchLoading(false);
        if (!data) return;

        const options: Option[] = (data.items ?? []).map((u: User) => ({
            label: u.fullName?.trim() ? `${u.fullName} - ${u.phoneNumber}` : u.phoneNumber || 'بدون شماره',
            value: u.phoneNumber,
            disabled: !u.phoneNumber,
        }));
        setPage(nextPage);
        setHasMore(nextPage < (data.pagination?.totalPages ?? 0));
        setUserOptions((prev) => {
            const merged = nextPage === 1 ? options : [...prev, ...options];
            const seen = new Set<string>();
            return merged.filter((o) => o.value && !seen.has(o.value) && seen.add(o.value));
        });
    };

    useEffect(() => {
        loadUsers(1, '');
        // هر دو با یک فلگ لودینگ؛ «همه» که ریپازیتوری اول لیست می‌گذارد اینجا لازم نیست.
        getAllLevels(setMetaLoading, (items: EnumAsString[]) => setLevels(items.filter((l) => l.id !== '0')));
        getAllUserCategories(setMetaLoading, (items: Enum[]) => setCategories(items.filter((c) => c.id !== 0)));

        return () => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onUserSearch = (value: string) => {
        setSearchValue(value);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => loadUsers(1, faNumToEnNum(value).trim()), 700);
    };

    const onPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;
        const isBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 10;
        if (isBottom && !searchLoading && hasMore) loadUsers(page + 1, faNumToEnNum(searchValue).trim());
    };

    const loadPhonesFromFilters = async () => {
        if (!levelId && !categoryId && !registerFrom && !registerTo) {
            message.warning('حداقل یکی را مشخص کنید: سطح کاربری، دسته کاربری یا بازه تاریخ ثبت‌نام.');
            return;
        }

        const params: string[] = [];
        if (levelId) params.push(`LevelId=${levelId}`);
        if (categoryId) params.push(`userCategory=${categoryId}`);
        if (registerFrom) params.push(`startDate=${jFormat(registerFrom)}`);
        if (registerTo) params.push(`endDate=${jFormatEndOfDay(registerTo)}`);
        const query = `?${params.join('&')}`;

        setFetchPhonesLoading(true);
        const phones = new Set<string>();
        for (let pageIdx = 1; pageIdx <= MAX_BULK_PHONE_FETCH_PAGES; pageIdx += 1) {
            const data = await getUsersPage(pageIdx, BULK_PHONES_PAGE_SIZE, query);
            if (!data) {
                setFetchPhonesLoading(false);
                message.error('دریافت لیست کاربران با خطا مواجه شد.');
                return;
            }
            const items = data.items ?? [];
            items.forEach((u) => u.phoneNumber?.trim() && phones.add(u.phoneNumber.trim()));
            if (items.length < BULK_PHONES_PAGE_SIZE || pageIdx >= (data.pagination?.totalPages ?? 0)) break;
        }
        setFetchPhonesLoading(false);

        if (!phones.size) {
            message.warning('شماره‌ای مطابق فیلترها یافت نشد.');
            return;
        }
        setManualInput(Array.from(new Set([...parseDistinctLines(manualInput), ...Array.from(phones)])).join('\n'));
        message.success(`${fa(phones.size)} شماره اضافه شد.`);
    };

    const resetForm = () => {
        setSelectedPhoneNumbers([]);
        setManualInput('');
        setSmsMessage('');
    };

    /** پارت‌ها را پشت سر هم می‌فرستد و پارت‌هایی را که درخواستشان شکست خورده برمی‌گرداند. */
    const sendChunks = async (chunks: Chunk[], current: Batch) => {
        const failed: Chunk[] = [];
        for (let i = 0; i < chunks.length; i += 1) {
            const chunk = chunks[i];
            const result = await sendBulkSmsPart({
                phoneNumbers: chunk.receivers,
                message: current.message,
                senderLine: current.senderLine,
                batchId: current.batchId,
                partNumber: chunk.partNumber,
                totalParts: current.totalParts,
            });

            if (!result) failed.push(chunk);
            else
                setProgress(
                    (prev) =>
                        prev && {
                            ...prev,
                            processed: prev.processed + chunk.receivers.length,
                            accepted: prev.accepted + result.sentCount,
                            failed: prev.failed + result.failedCount,
                            unresolved: [...prev.unresolved, ...(result.unresolvedUserIds ?? [])],
                        },
                );

            if (i < chunks.length - 1) await new Promise((resolve) => setTimeout(resolve, REQUEST_DELAY_MS));
        }
        return failed;
    };

    const runSend = async (chunks: Chunk[], current: Batch) => {
        setSubmitting(true);
        const failed = await sendChunks(chunks, current);
        setSubmitting(false);
        setFailedChunks(failed);

        if (failed.length) {
            message.warning('بخشی از ارسال‌ها ناموفق بود. می‌توانید فقط پارت‌های ناموفق را دوباره تلاش کنید.');
            return;
        }
        message.success('ارسال پیامک انجام شد.');
        resetForm();
    };

    const onSubmit = () => {
        if (!receivers.length) return message.warning('حداقل یک گیرنده انتخاب کنید.');
        if (!smsMessage.trim()) return message.warning('متن پیامک را وارد کنید.');
        setConfirmOpen(true);
    };

    const onConfirm = () => {
        setConfirmOpen(false);

        const chunks: Chunk[] = [];
        for (let i = 0; i < receivers.length; i += MAX_RECEIVERS_PER_REQUEST)
            chunks.push({
                partNumber: chunks.length + 1,
                receivers: receivers.slice(i, i + MAX_RECEIVERS_PER_REQUEST),
            });

        const current: Batch = {
            batchId: newBatchId(),
            totalParts: chunks.length,
            message: smsMessage.trim(),
            senderLine,
        };
        setBatch(current);
        setFailedChunks([]);
        setProgress({ total: receivers.length, processed: 0, accepted: 0, failed: 0, unresolved: [] });
        runSend(chunks, current);
    };

    const onRetryFailedChunks = () => {
        if (!batch || !failedChunks.length || submitting) return;
        runSend(failedChunks, batch);
    };

    return (
        <div className="sms-send">
            <Modal isOpen={confirmOpen} setIsOpen={setConfirmOpen} className="sms-confirm-modal">
                <h4>ارسال پیامک گروهی</h4>
                <p>
                    پیامک برای <strong>{fa(receivers.length)}</strong> گیرنده در <strong>{fa(partsCount)}</strong> پارت
                    از خط <strong className="en">{senderLine}</strong> ارسال شود؟
                </p>
                <div className="sms-preview">{smsMessage.trim()}</div>
                <div className="buttons">
                    <Button type={buttonType.info} text="ارسال" icon={<SendOutlined />} onClick={onConfirm} />
                    <Button type={buttonType.outlined} text="انصراف" onClick={() => setConfirmOpen(false)} />
                </div>
            </Modal>

            <section className="sms-section">
                <header className="sms-section__head">
                    <h3>
                        <TeamOutlined /> گیرنده‌ها
                    </h3>
                    <p>
                        گیرنده‌ها را از فیلتر کاربران، جستجوی کاربر یا لیست دستی انتخاب کنید؛ تکراری‌ها یک بار ارسال
                        می‌شوند.
                    </p>
                </header>

                <div className="sms-filters">
                    <div className="sms-filters__title">
                        <FilterOutlined /> افزودن شماره‌ها از فیلتر کاربران
                    </div>
                    <div className="sms-filters__grid">
                        <div className="sms-field">
                            <label>سطح کاربری</label>
                            <Select
                                allowClear
                                loading={metaLoading}
                                placeholder="همه سطوح"
                                value={levelId}
                                onChange={(value) => setLevelId(value)}
                                options={levels.map((l) => ({ label: l.title, value: l.id }))}
                            />
                        </div>
                        <div className="sms-field">
                            <label>دسته کاربری</label>
                            <Select
                                allowClear
                                loading={metaLoading}
                                placeholder="انتخاب نشده"
                                value={categoryId}
                                onChange={(value) => setCategoryId(value)}
                                options={categories.map((c) => ({ label: c.title, value: c.id }))}
                            />
                        </div>
                        <div className="sms-field">
                            <label>ثبت‌نام از تاریخ</label>
                            <DatePicker
                                value={registerFrom}
                                calendar={persian}
                                locale={persian_fa}
                                render={
                                    <div>
                                        <Input
                                            value={registerFrom}
                                            icon={<CalendarIcon />}
                                            placeholder="از تاریخ"
                                            readOnly
                                        />
                                    </div>
                                }
                                onChange={(date: any) => setRegisterFrom(date ? date.format() : '')}
                            />
                        </div>
                        <div className="sms-field">
                            <label>ثبت‌نام تا تاریخ</label>
                            <DatePicker
                                value={registerTo}
                                calendar={persian}
                                locale={persian_fa}
                                render={
                                    <div>
                                        <Input
                                            value={registerTo}
                                            icon={<CalendarIcon />}
                                            placeholder="تا تاریخ"
                                            readOnly
                                        />
                                    </div>
                                }
                                onChange={(date: any) => setRegisterTo(date ? date.format() : '')}
                            />
                        </div>
                    </div>
                    <div className="sms-filters__actions">
                        <Button
                            type={buttonType.secondary}
                            text="دریافت شماره‌ها"
                            loading={fetchPhonesLoading}
                            disabled={submitting || fetchPhonesLoading}
                            onClick={loadPhonesFromFilters}
                        />
                    </div>
                </div>

                <div className="sms-field">
                    <label>جستجوی کاربر</label>
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        filterOption={false}
                        loading={searchLoading}
                        placeholder="نام یا شماره موبایل کاربر را جستجو کنید"
                        notFoundContent={searchLoading ? 'در حال جستجو...' : 'یافت نشد'}
                        value={selectedPhoneNumbers}
                        options={userOptions}
                        onSearch={onUserSearch}
                        onChange={(values: string[]) => setSelectedPhoneNumbers(values)}
                        onPopupScroll={onPopupScroll}
                        dropdownRender={(menu) => (
                            <>
                                {menu}
                                {searchLoading && page > 1 && (
                                    <div className="sms-select-loading">
                                        <Spin size="small" />
                                    </div>
                                )}
                            </>
                        )}
                    />
                </div>

                <div className="sms-field">
                    <label>شماره‌ها یا شناسه‌ی کاربران (هر مورد در یک خط)</label>
                    <AntInput.TextArea
                        className="sms-textarea en"
                        value={manualInput}
                        onChange={(e) => setManualInput(e.target.value)}
                        placeholder={'09120000000\n09350000000'}
                        autoSize={{ minRows: 5, maxRows: 12 }}
                        dir="ltr"
                    />
                    <div className="sms-stats" role="group" aria-label="آمار خطوط شماره‌ها">
                        <div className="sms-stat sendable">
                            <span>معتبر</span>
                            <strong>{fa(manualStats.sendable)}</strong>
                        </div>
                        <div className="sms-stat">
                            <span>کل خطوط</span>
                            <strong>{fa(manualStats.total)}</strong>
                        </div>
                        <div className="sms-stat duplicates">
                            <span>حذف تکراری</span>
                            <strong>{fa(manualStats.duplicates)}</strong>
                        </div>
                        <div className="sms-stat empty">
                            <span>خطوط خالی</span>
                            <strong>{fa(manualStats.empty)}</strong>
                        </div>
                    </div>
                </div>
            </section>

            <section className="sms-section">
                <header className="sms-section__head">
                    <h3>
                        <SendOutlined /> پیام
                    </h3>
                    <p>
                        گیرنده‌ها در پارت‌های {fa(MAX_RECEIVERS_PER_REQUEST)}تایی ارسال می‌شوند و وضعیت هر پارت در تب
                        «سوابق ارسال» ثبت می‌شود.
                    </p>
                </header>

                <div className="sms-field">
                    <label>خط ارسال</label>
                    <Select
                        className="en"
                        value={senderLine}
                        onChange={(value) => setSenderLine(String(value))}
                        options={SENDER_LINES.map((line) => ({ label: line, value: line }))}
                    />
                </div>

                <div className="sms-field">
                    <label>متن پیامک</label>
                    <AntInput.TextArea
                        className="sms-textarea"
                        value={smsMessage}
                        onChange={(e) => setSmsMessage(e.target.value)}
                        placeholder="متن پیامک را وارد کنید"
                        autoSize={{ minRows: 6, maxRows: 14 }}
                    />
                    <div className="sms-counter">
                        <span>{fa(smsLength.length)} کاراکتر</span>
                        <span>{fa(smsLength.parts)} پیامک</span>
                    </div>
                </div>

                <div className="sms-summary">
                    <div>
                        <span>گیرنده‌ها</span>
                        <strong>{fa(receivers.length)}</strong>
                    </div>
                    <div>
                        <span>پارت‌ها</span>
                        <strong>{fa(partsCount)}</strong>
                    </div>
                </div>

                {progress && (
                    <div className="sms-progress">
                        <div className="sms-progress__counts">
                            <span>
                                {fa(progress.processed)} از {fa(progress.total)} پردازش شد
                            </span>
                            <span className="accepted">
                                <CheckCircleFilled /> {fa(progress.accepted)} پذیرفته
                            </span>
                            <span className="failed">
                                <CloseCircleFilled /> {fa(progress.failed)} ناموفق
                            </span>
                        </div>
                        <Progress
                            percent={Math.min(100, Math.round((progress.processed / progress.total) * 100))}
                            status={failedChunks.length ? 'exception' : submitting ? 'active' : 'normal'}
                        />
                        {!!failedChunks.length && (
                            <p className="sms-progress__error">
                                درخواست {fa(failedChunks.length)} پارت ناموفق بود و برای گیرنده‌های آن پیامکی ارسال نشد.
                            </p>
                        )}
                        {!!progress.unresolved.length && (
                            <p className="sms-progress__error">
                                برای {fa(progress.unresolved.length)} شناسه‌ی کاربری، کاربر یا شماره موبایلی پیدا نشد.
                            </p>
                        )}
                        {batch && !submitting && progress.processed > 0 && (
                            <button type="button" className="sms-link" onClick={() => onShowHistory(batch.batchId)}>
                                مشاهده‌ی این ارسال در سوابق
                            </button>
                        )}
                    </div>
                )}

                <div className="sms-actions">
                    {!!failedChunks.length && (
                        <Button
                            type={buttonType.outlined}
                            text="تلاش مجدد پارت‌های ناموفق"
                            loading={submitting}
                            disabled={submitting}
                            onClick={onRetryFailedChunks}
                        />
                    )}
                    <Button
                        type={buttonType.info}
                        text="ارسال پیامک"
                        icon={<SendOutlined />}
                        loading={submitting}
                        disabled={submitting}
                        onClick={onSubmit}
                    />
                </div>
            </section>
        </div>
    );
};
